import React from "react";

export type DateStyle = "short" | "long" | "full" | "weekday" | "year";

/**
 * Represents the context for handling dates.
 */
export interface IDateContext {
  /**
   * Formats a date value according to the specified style.
   * @param value The date value to format.
   * @param style The style to use for formatting.
   * @returns The formatted date string.
   */
  formatDate(value: Date, style: DateStyle): string;
}

export class FormatDateContext implements IDateContext {
  private _yearFormat?: Intl.DateTimeFormat;
  private _weekdayFormat?: Intl.DateTimeFormat;
  private _shortFormat?: Intl.DateTimeFormat;
  private _longFormat?: Intl.DateTimeFormat;
  private _dateTimeFormat?: Intl.DateTimeFormat;

  private get yearFormat(): Intl.DateTimeFormat {
    if (!this._yearFormat) {
      this._yearFormat = Intl.DateTimeFormat(this.locale, { year: "numeric" });
    }
    return this._yearFormat;
  }

  private get weekdayFormat(): Intl.DateTimeFormat {
    if (!this._weekdayFormat) {
      this._weekdayFormat = Intl.DateTimeFormat(this.locale, { weekday: "long" });
    }
    return this._weekdayFormat;
  }

  private get shortFormat(): Intl.DateTimeFormat {
    if (!this._shortFormat) {
      this._shortFormat = Intl.DateTimeFormat(this.locale, { month: "short", day: "numeric" });
    }
    return this._shortFormat;
  }

  private get longFormat(): Intl.DateTimeFormat {
    if (!this._longFormat) {
      this._longFormat = Intl.DateTimeFormat(this.locale, { year: "numeric", month: "short", day: "numeric" });
    }
    return this._longFormat;
  }

  private get dateTimeFormat(): Intl.DateTimeFormat {
    if (!this._dateTimeFormat) {
      this._dateTimeFormat = Intl.DateTimeFormat(this.locale, {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric"
      });
    }
    return this._dateTimeFormat;
  }

  constructor(private locale: string) {}

  formatDate(value: Date, style: DateStyle): string {
    let formattedDate: string;
    if (style === "short") {
      formattedDate = this.shortFormat.format(value);
    } else if (style === "full") {
      formattedDate = this.dateTimeFormat.format(value);
    } else if (style === "year") {
      formattedDate = this.yearFormat.format(value);
    } else if (style === "weekday") {
      formattedDate = this.weekdayFormat.format(value);
    } else {
      formattedDate = this.longFormat.format(value);
    }

    return formattedDate.replace("\u{202f}", " ");
  }
}

export const DateContext = React.createContext<IDateContext>(new FormatDateContext("en-us"));
